import {formatSubDetails} from './util'

// formatSubDetails prices Keap tiered add-ons (Additional Contacts, Text &
// Voice) only for the Keap Add-Ons wrapper passed in `addons`. A wrapper passed
// in `plan` keeps its raw children, so those tiers render as $0.00.
const isKeapAddonsWrapper = item =>
  String(item?.code || '').startsWith('addon-5-keap-addons')
const splitKeapAddonsWrapper = subscriptions => ({
  plan: subscriptions.filter(item => !isKeapAddonsWrapper(item)),
  addons: subscriptions.filter(isKeapAddonsWrapper),
})

const subscriptionLines = payload => [
  ...(payload?.parentSubscriptions || []),
  ...(payload?.childSubscriptions || []),
]

// Loads, per location, the subscriptions the account already holds — only for
// locations that have lines the link marks as already added, so a link of all-new
// items makes no extra calls. This is the same data the core OOE payment link
// (ooe-app) prices owned items from. A failed lookup leaves that location empty,
// so its lines simply keep their catalog price.
export const fetchOwnedSubscriptionsByLocation = async (
  client,
  corePaymentPayload,
) => {
  const locationThryvIds = [
    ...new Set(
      subscriptionLines(corePaymentPayload)
        .filter(line => line?.isAlreadyAdded && line.locationThryvId)
        .map(line => line.locationThryvId),
    ),
  ]
  const entries = await Promise.all(
    locationThryvIds.map(async locationThryvId => {
      try {
        const response = await client(
          `accounts/5/subscriptions?thryvId=${encodeURIComponent(locationThryvId)}`,
        )
        return [
          locationThryvId,
          [
            ...(response?.childSubscriptions || []),
            ...(response?.parentSubscriptions || []),
          ],
        ]
      } catch (err) {
        console.log('Error fetching owned subscriptions', locationThryvId, err)
        return [locationThryvId, []]
      }
    }),
  )
  return Object.fromEntries(entries)
}

// What each location of a multi-location order will actually be charged today
// (prorated, discounted, with tax), from the backend's per-location preview.
// ooe-service prices the whole order against one Recurly account, which Recurly
// rejects when locations repeat a plan, so it returns no preview for those orders
// by default; `previewByLocation` asks it to price each location on its own.
// Null when there is nothing to show (failure, billing info not on file yet, or
// the backend does not offer it): the estimate keeps being used.
export const getPurchasePlanPath = (paymentUuid, byLocation = false) =>
  `cc/order/purchase-plan/${paymentUuid}${byLocation ? '?previewByLocation=true' : ''}`

export const fetchLocationPreviews = async (client, paymentUuid) => {
  if (!paymentUuid) return null
  try {
    const response = await client(getPurchasePlanPath(paymentUuid, true))
    return response?.locationPreviews || null
  } catch (err) {
    console.log('Error fetching location previews', err)
    return null
  }
}

// Gives every location its backend preview as `location.preview`. All or nothing:
// a total built from only some locations would be wrong, so unless every location
// has one the cart is returned untouched. Never mutates the cart.
export const attachLocationPreviews = (cartObj, locationPreviews) => {
  const locations = cartObj?.locations ?? []
  if (
    locations.length < 2 ||
    !locationPreviews ||
    !locations.every(location =>
      Number.isFinite(
        Number(locationPreviews[location.locationThryvId]?.total),
      ),
    )
  ) {
    return cartObj
  }
  return {
    ...cartObj,
    locations: locations.map(location => ({
      ...location,
      preview: locationPreviews[location.locationThryvId],
    })),
  }
}

// Business names for a multi-location link's section headers, so every page shows
// "Fenway" rather than a thryv id. Only looked up for 2+ locations (a single
// location shows no header). Display-only: a failed or nameless lookup leaves that
// location out and its header falls back to the thryv id.
export const fetchLocationNames = async (client, corePaymentPayload) => {
  const locationDetails = corePaymentPayload?.locationDetails || []
  if (locationDetails.length < 2) return {}
  const entries = await Promise.all(
    locationDetails.map(async ({locationThryvId}) => {
      try {
        const account = await client(
          `orders/validate-emailaccount/${encodeURIComponent(locationThryvId)}`,
        )
        return [locationThryvId, account?.company]
      } catch {
        return [locationThryvId, null]
      }
    }),
  )
  return Object.fromEntries(entries.filter(([, name]) => name))
}

// Lines the link marks as already added belong to the customer already: they are
// not charged today, and Due Monthly must show what the account really pays for
// them (a plan can be $0 or discounted on the account), not its catalog price.
// Sets `ownedUnitAmount` from the subscription that location holds for the same
// plan — matched per location, since the same plan can be new at one location and
// owned at another. Never mutates the link; a line with no matching subscription
// is returned as is and keeps its catalog price.
export const applyOwnedSubscriptionPrices = (
  corePaymentPayload,
  subscriptionsByLocation = {},
) => {
  const withOwnedPrice = line => {
    if (!line?.isAlreadyAdded) return line
    const owned = (subscriptionsByLocation?.[line.locationThryvId] || []).find(
      subscription => subscription?.code === line.code,
    )
    if (!owned) return line
    const quantity = Number(owned.quantity) || Number(line.quantity) || 1
    // Pre-tax line amount (after any recurring discount): the summary labels its
    // amounts "+ Tax", and a subscription's `total` already includes tax.
    const amount = [owned.subtotal, owned.total].find(value =>
      Number.isFinite(Number(value ?? NaN)),
    )
    const lineAmount =
      amount !== undefined
        ? Number(amount)
        : Number(owned.unitAmount) * quantity
    return Number.isFinite(lineAmount)
      ? {...line, ownedUnitAmount: lineAmount / quantity}
      : line
  }
  return {
    ...corePaymentPayload,
    parentSubscriptions: (corePaymentPayload?.parentSubscriptions || []).map(
      withOwnedPrice,
    ),
    childSubscriptions: (corePaymentPayload?.childSubscriptions || []).map(
      withOwnedPrice,
    ),
  }
}

// Core OOE parent-pay links persist the exact send-link payload with the
// generated Keap Partner link. Those payloads are grouped as Recurly
// subscriptions (parentSubscriptions/childSubscriptions), rather than the
// legacy KP record's plan_code/add_on_code fields. Convert the grouped payload
// into the cart shape consumed by the payment summary without dropping the
// location tag on any line item.
export const buildCorePaymentCart = (
  corePaymentPayload,
  subscriptionList,
  currency,
) => {
  const catalogItems = [
    ...(subscriptionList?.centers || []),
    ...(subscriptionList?.apps || []),
    ...(subscriptionList?.oneTimeFees || []),
  ]
  const findCatalogItem = code => catalogItems.find(item => item.code === code)

  const withCatalogDetails = item => {
    const catalogItem = findCatalogItem(item?.code)
    const currencyDetail = (catalogItem?.currencies || []).find(
      itemCurrency => itemCurrency.currency === currency,
    )

    return {
      ...item,
      quantity: item?.quantity ?? currencyDetail?.quantity ?? 1,
      name: item?.name || catalogItem?.name || item?.code,
      unitAmount:
        item?.unitAmount ?? item?.amount ?? currencyDetail?.unitAmount ?? 0,
      prodType: item?.prodType || catalogItem?.prod_type,
      addons: (item?.addons || []).map(withCatalogDetails),
    }
  }

  const parentSubscriptions = (
    corePaymentPayload?.parentSubscriptions || []
  ).map(withCatalogDetails)
  const childSubscriptions = (corePaymentPayload?.childSubscriptions || []).map(
    withCatalogDetails,
  )
  const oneTimeFees = (corePaymentPayload?.oneTimeFees || []).map(
    withCatalogDetails,
  )
  const cart = formatSubDetails(
    {
      ...splitKeapAddonsWrapper([
        ...parentSubscriptions,
        ...childSubscriptions,
      ]),
      oneTimeFees,
      coupons: [],
    },
    subscriptionList,
    currency,
  )

  // formatSubDetails rebuilds every line without its locationThryvId, so the
  // flat cart above can't be split by location afterwards. Build one cart per
  // location from the still-tagged lines so the summary can show each
  // location's own items/total, like the rep-side Order Summary does.
  const locationIds = [
    ...new Set(
      [...parentSubscriptions, ...childSubscriptions, ...oneTimeFees]
        .map(item => item?.locationThryvId)
        .filter(Boolean),
    ),
  ]
  const locations =
    locationIds.length > 1
      ? locationIds.map(locationThryvId => {
          const forLocation = item => item?.locationThryvId === locationThryvId
          const locationProductSub = {
            parentSubscriptions: parentSubscriptions.filter(forLocation),
            childSubscriptions: childSubscriptions.filter(forLocation),
            oneTimeFees: oneTimeFees.filter(forLocation),
          }
          return {
            locationThryvId,
            cart: {
              isCoreLink: true,
              ...formatSubDetails(
                {
                  ...splitKeapAddonsWrapper([
                    ...locationProductSub.parentSubscriptions,
                    ...locationProductSub.childSubscriptions,
                  ]),
                  oneTimeFees: locationProductSub.oneTimeFees,
                  coupons: [],
                },
                subscriptionList,
                currency,
              ),
              productSub: locationProductSub,
            },
          }
        })
      : []

  return {
    ...cart,
    isCoreLink: true,
    productSub: {
      parentSubscriptions,
      childSubscriptions,
      oneTimeFees,
    },
    locations,
  }
}

// A multi-location link can carry each location's own coupons (locationDetails
// [].coupons). Show them inside that location, like the rep's Order Summary does,
// instead of one order-level block under all the locations. Links without
// per-location coupons, and single-location links, keep the order-level list.
// `coupons` are the validated coupon objects; a code that did not validate is
// dropped. Never mutates the cart.
export const attachLocationCoupons = (
  cartObj,
  corePaymentPayload,
  coupons = [],
) => {
  const couponsByCode = Object.fromEntries(
    coupons.map(coupon => [coupon.code, coupon]),
  )
  const locationCouponCodes = Object.fromEntries(
    (corePaymentPayload?.locationDetails || [])
      .filter(detail => Array.isArray(detail.coupons))
      .map(detail => [detail.locationThryvId, detail.coupons]),
  )
  if (!cartObj?.locations?.length || !Object.keys(locationCouponCodes).length) {
    return cartObj
  }
  return {
    ...cartObj,
    locations: cartObj.locations.map(location => ({
      ...location,
      coupons: (locationCouponCodes[location.locationThryvId] || [])
        .map(code => couponsByCode[code])
        .filter(Boolean),
    })),
  }
}

// Codes the link's coupons are validated against: every plan/add-on/one-time fee
// on the link except the Command Center (cc*) base plans.
export const getCorePaymentPlanCodes = cartObj => {
  const coreCodes = [
    ...cartObj.productSub.parentSubscriptions,
    ...cartObj.productSub.childSubscriptions,
  ].flatMap(sub => [sub.code, ...(sub.addons || []).map(a => a.code)])
  coreCodes.push(...cartObj.productSub.oneTimeFees.map(f => f.code))
  return [...new Set(coreCodes.filter(code => code && !code.startsWith('cc')))]
}
